import React, {
	useCallback,
	useState,
} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import CustomIcon from '../Common/CustomIcon/CustomIcon';
import CustomCheckbox from './Elements/Checkbox';
import {
	v4 as uuidv4,
} from 'uuid';

// Styles
import style from './Listings.module.css';

import {
	getFDValue,
} from 'Vulture/Helpers';

import {
	getSession,
} from 'Vulture/Session';

import {
	hasGlobalScope,
	isNarrowScoped,
} from 'Vulture/Permissions';
import {
	dlToggleCreateAnotherListing,
} from 'Vulture/DataLayer';
import {
	deleteListings,
} from 'Vulture/Content';
import {
	parseLandmarkID,
	stringifyLandmarkID,
} from './Listings';
import {
	formatTimeForPicker,
	handleStartTimeChange,
	handleEndTimeChange,
	formatDateForJSON,
	formateDateForDatePicker,
	dayMap,
	timePickerProps,
	WeekdaySelector,
	getInitialSchedulingMode,
	cleanSchedulingData,
	getSchedulingModeDisplayText,
	getSchedulingPlaceholderStyle,
	SchedulingHelperText,
} from './utils/scheduling';
import {
	ListingConfigContext,
} from './ListingConfigContext.js';

export const LandmarkIDField = (props) => {
	const {
		app,
		fd,
		entityName,
		label,
	} = props;

	const initialValue = getFDValue(fd, entityName) || '[""]';

	const [value, setValue,] = useState(parseLandmarkID(initialValue));

	return (
		<div className={`text-small`}>
			<div className="label">
				<label>{label}</label>
			</div>
			<input
				type='text'
				value={value}
				data-test-id="landmarkIDInput"
				onChange={(e) => {
					if (value === e.target.value) {
						return;
					}

					setValue(e.target.value);
				}}
				onBlur={(e) => {
					const newValue = stringifyLandmarkID(e.target.value.trim());

					if (newValue === getFDValue(fd, entityName)) {
						return;
					}

					app.setValue('listing', entityName, newValue);
				}}
			/>
		</div>
	);
};

const PARENT_LABEL_PREVIEW_COUNT = 3;

export const ParentLabels = (props) => {
	const {
		controlId,
		labels,
	} = props;

	const [ expanded, setExpanded, ] = useState(false);

	const named = Array.isArray(labels) ?
		labels.filter((name) => {
			return typeof name === 'string' && name.trim() !== '';
		}) :
		[];

	if (named.length === 0) {
		return null;
	}

	const isTruncatable = named.length > PARENT_LABEL_PREVIEW_COUNT;
	const shown = (isTruncatable && !expanded) ?
		named.slice(0, PARENT_LABEL_PREVIEW_COUNT) :
		named;

	return (
		<>
			<label
				htmlFor={controlId}
				className={style.assignInlineItemSubLabel}
				data-test-id="assignInlineParentLabels"
			>
				GN: {shown.join(', ')}{isTruncatable && !expanded ? '…' : ''}
			</label>
			{isTruncatable && (
				<button
					type="button"
					className={style.assignInlineParentLabelsToggle}
					data-test-id="assignInlineParentLabelsToggle"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setExpanded(!expanded);
					}}
				>
					{expanded ? 'Show Less' : 'Show All'}
				</button>
			)}
		</>
	);
};

class ListingsModal extends React.Component {
	static contextType = ListingConfigContext;

	constructor(props) {
		super(props);

		let scheduled = false;
		let scheduling = {
			days: [],
			start_date: null,
			end_date: null,
			start_time: null,
			end_time: null,
			recurring_start_time: null,
			recurring_end_time: null,
			recurring_times_by_day: {},
		};
		let initialSchedulingMode = '';

		const {
			listingFD,
			listing,
		} = props;

		const schedulingJSON = listingFD?.[`listing.${listing.uuid}`]?.listing_scheduling;

		if (schedulingJSON !== undefined) {
			const parsedSchedulingJSON = JSON.parse(schedulingJSON);

			if (parsedSchedulingJSON.scheduled !== undefined) {
				// New format
				scheduled = parsedSchedulingJSON.scheduled || false;
				scheduling = {
					...scheduling,
					...(parsedSchedulingJSON.scheduling || {}),
				};
				initialSchedulingMode = parsedSchedulingJSON.schedulingMode ?? getInitialSchedulingMode(parsedSchedulingJSON.scheduling);
			} else if (parsedSchedulingJSON.scheduling) {
				// Old format - migrate
				scheduled = parsedSchedulingJSON.scheduling;
				if (parsedSchedulingJSON.use_dates) {
					scheduling.start_date = parsedSchedulingJSON.start_date || null;
					scheduling.end_date = parsedSchedulingJSON.end_date || null;
				}
				const hasSelectedDays = parsedSchedulingJSON.use_days &&
					(parsedSchedulingJSON.days || []).length > 0;
				if (hasSelectedDays) {
					scheduling.days = parsedSchedulingJSON.days;
					initialSchedulingMode = 'all-day-specific-days';
				}
				if (parsedSchedulingJSON.use_times) {
					scheduling.recurring_start_time = parsedSchedulingJSON.start_time || null;
					scheduling.recurring_end_time = parsedSchedulingJSON.end_time || null;
					if (!hasSelectedDays) {
						initialSchedulingMode = 'daily-time-range';
					}
				}
			}
		}

		this.state = {
			name: '',
			suite: '',
			elms: [],
			selectedContentCount: 0,
			assignContentData: [],
			associatedComponents: [],
			unassociatedComponents: [],
			// activeListingUUID: '',
			originalAssociatedComponents: [],
			confirmModal: false,
			confirmModalContent: '',
			createNewListingAfterThisOne: false,
			formerAssociatedComponents: [],
			formerUnassociatedComponents: [],
			requiredFields: [],

			listing,
			listingParents: [],

			scheduled,
			scheduling,
			schedulingMode: initialSchedulingMode,
			schedulingModeDropdownOpen: false,
			assignSearch: '',
		};

		this.scrollToRef = React.createRef();
		this.schedulingModeDropdownRef = React.createRef();
		this.schedulingSectionRef = React.createRef();
		this.timeRangesByDayRef = React.createRef();
		this.assignInlinePropertyRefs = {};
	}

	componentDidMount() {
		const {
			listingGroups,
		} = this.props;
		this.makeMaps();
		this.makeFields();

		this.filteredListingGroups = listingGroups
			.filter((listingGroup) => {
				return listingGroup.nonExclusive;
			});

		document.addEventListener('mousedown', this.handleClickOutside);
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleClickOutside);
	}

	handleClickOutside = (event) => {
		if (this.schedulingModeDropdownRef.current && !this.schedulingModeDropdownRef.current.contains(event.target)) {
			this.setState({
				schedulingModeDropdownOpen: false,
			});
		}
	}

	toggleSchedulingModeDropdown = () => {
		this.setState((prevState) => {
			return {
				schedulingModeDropdownOpen: !prevState.schedulingModeDropdownOpen,
			};
		});
	}

	componentDidUpdate(prevProps, prevState) {
		const {
			listingFD,
			app,
		} = this.props;

		let {
			scheduled,
			scheduling,
			schedulingMode,
			listing,
		} = this.state;

		if (JSON.stringify(prevProps.listingFD) !== JSON.stringify(listingFD)) {
			this.makeMaps();
			this.makeFields();
		}

		if (prevState.scheduled !== scheduled ||
			prevState.scheduling !== scheduling ||
			prevState.schedulingMode !== schedulingMode) {

			let modeToSave = schedulingMode;
			if (!scheduled) {
				modeToSave = '';
			}

			const cleanedScheduling = cleanSchedulingData(scheduling, scheduled, modeToSave);

			const listingScheduling = {
				scheduled,
				scheduling: cleanedScheduling,
				schedulingMode: modeToSave,
			};

			const keys = [
				`listing.${listing.uuid}.listing_scheduling`,
			];

			const vals = [
				JSON.stringify(listingScheduling),
			];

			app.setValue('listings', keys, vals);
		}
	}

	makeMaps() {
		const {
			listingToListingCollectionsMap,
			listingGroups,
			findRootListings,
			app,
			listingFD,
			listingConfigToListingsMap,
			activeListingConfig,
		} = this.props;

		const {
			listing,
		} = this.state;

		const { listingConfigMap, } = this.context;

		let listingM1Key = `listing.${listing?.uuid}`;
		let listingParents = [];

		// T1 listings
		if (activeListingConfig?.root) {
			for (let group of Object.values(listingGroups || {})) {
				if (Array.isArray(group.listingCollectionM1Keys) && group.listingCollectionM1Keys.length > 0) {
					let parentM1Key = group.listingCollectionM1Keys[0];
					let entityName = `${parentM1Key}.${listingM1Key}`;
					let relVal = getFDValue(listingFD, entityName);
					let checked = typeof relVal !== 'undefined';

					let parentObj = {
						checked,
						label: group.name || '',
						uuid: group.uuid || '',
						listingCollectionM1Keys: group.listingCollectionM1Keys,
						parentM1Key,
						listingM1Key,
						entityName,
						t1: true,
					};

					listingParents.push(parentObj);
				}
			}
		}

		// T1+
		if (!activeListingConfig?.root) {
			let rootConfig;
			for (const uuid in listingConfigMap || {}) {
				if (listingConfigMap[uuid]?.root === true) {
					rootConfig = uuid;
					break;
				}
			}

			let parentListingConfigUUID = activeListingConfig?.parentUUID;
			let parentListingConfigM1Key = `listing_config.${parentListingConfigUUID}`;
			let rootParentListingConfigKey = `listing_config.${rootConfig}`;
			let parentListings = listingConfigToListingsMap?.[parentListingConfigM1Key];
			let rootParentListings = listingConfigToListingsMap?.[rootParentListingConfigKey];

			const {
				parentsFirstColumnEntityKey,
				parentsFirstColumnAttr,
			} = this.props.parentsPrimaryColumnData || {};

			if (parentListings) {
				for (const parentM1Key of Object.keys(parentListings)) {
					let parentListingCollectionM1Keys = [];
					let rootListings = findRootListings(parentM1Key) || [];
					if (isNarrowScoped()) {
						let hideListing = false;
						for (let rootListing of rootListings) {
							if (
								rootParentListings?.[rootListing]?.systems?.length === 0
							) {
								hideListing = true;
							} else if (listingToListingCollectionsMap[rootListing]) {
								parentListingCollectionM1Keys.push(
									listingToListingCollectionsMap[rootListing]
								);
								hideListing = false;
							}
						}

						if (hideListing) {
							continue; // Skip to the next iteration
						}
					}

					for (let rootListing of rootListings) {
						if (listingToListingCollectionsMap[rootListing]) {
							parentListingCollectionM1Keys.push(
								listingToListingCollectionsMap[rootListing]
							);
						}
					}

					const label =
						listingFD?.[parentM1Key]?.[parentsFirstColumnEntityKey]?.[
						parentsFirstColumnAttr
						] || '';
					parentListingCollectionM1Keys = parentListingCollectionM1Keys.flat();

					let parentLabels = new Set();
					for (let listingCollectionM1Key of parentListingCollectionM1Keys) {
						for (let group of listingGroups || []) {
							if (
								Array.isArray(group.listingCollectionM1Keys) &&
								group.listingCollectionM1Keys.includes(
									listingCollectionM1Key
								)
							) {
								parentLabels.add(group.name);
							}
						}
					}

					const parentLabelsList = Array.from(parentLabels).sort((a, b) => {
						return `${a || ''}`.toLowerCase().localeCompare(`${b || ''}`.toLowerCase());
					});

					let entityName = `${parentM1Key}.${listingM1Key}`;
					let relVal = getFDValue(listingFD, entityName);
					let checked = typeof relVal !== 'undefined';

					let parentObj = {
						label,
						parentLabelsList,
						parentM1Key,
						listingM1Key,
						checked,
						entityName,
						t1: false,
					};

					listingParents.push(parentObj);
				}
			}
		}

		listingParents.sort((a, b) => {
			let aStr = a.label?.toLowerCase() || '';
			let bStr = b.label?.toLowerCase() || '';

			if (aStr > bStr) return 1;
			if (aStr < bStr) return -1;
			return 0;
		});

		const filteredListingParents = listingParents.filter(
			(listingParent) => {
				return listingParent.label && listingParent.label.trim() !== '';
			}
		);

		let selectedContentCount = filteredListingParents.filter((p) => {
			return p.checked;
		}).length;

		if (Array.isArray(this.state.listingParents)) {
			this.state.listingParents.forEach((listingParent, index) => {
				if (listingParent.checked && filteredListingParents[index]) {
					filteredListingParents[index].checked = true;
					app.setValue('listings', filteredListingParents[index].entityName, true);
				}
			});
		}

		this.setState({
			listingParents: filteredListingParents,
			assignContentData: filteredListingParents,
			selectedContentCount,
		});
	}


	makeFields() {
		const {
			app,
			listingFD,
			accountUUID,
			buildingsDataArray,
			listingConfigUUID,
			listingConfigToListingsMap,
			listingConfigIsIntegration,
			isEdit,
			activeListingConfig,
		} = this.props;

		const { listingConfigMap, } = this.context;

		const {
			listing,
			createNewListingAfterThisOne,
		} = this.state;

		let assignContentData = this.state.assignContentData;
		let placeholderContentData = this.state.assignContentData;
		let assignContentDataObject = {};

		let associatedComponents = [];
		let unassociatedComponents = [];
		if (createNewListingAfterThisOne) {
			this.state.assignContentData.forEach((assignedContent) => {
				if (assignedContent.checked) {
					associatedComponents.push(assignedContent.componentID);
				}
			});
		}

		if (!isEdit) {
			app.setValue('listings', [
				`account.${accountUUID}.listing.${listing.uuid}`,
				`listing.${listing.uuid}.listing_config.${listingConfigUUID}`,
			], [true, true,]);
		}

		if (!activeListingConfig.root) {
			const {
				parentsFirstColumnEntityKey, parentsFirstColumnAttr,
			} = this.props.parentsPrimaryColumnData;
			let parentEntityKey = `listing_config.${activeListingConfig.parentUUID}`;

			Object.keys(listingConfigToListingsMap[parentEntityKey]).forEach((listingM1KEy) => {
				let listingUUID = listingM1KEy.split('.')[1];
				let data = {
					componentID: listingUUID,
					name: this.props.getParentComponentName(listingM1KEy, parentsFirstColumnEntityKey, parentsFirstColumnAttr),
					checked: false,
				};

				if (this.props.isEdit && this.state.listing.parent_listings && this.state.listing.parent_listings.includes(`listing.${listingUUID}`)) {
					data.checked = true;
					if (!associatedComponents.includes(listingUUID)) {
						associatedComponents.push(listingUUID);
					}
				} else if (!unassociatedComponents.includes(listingUUID) && !associatedComponents.includes(listingUUID)) {
					unassociatedComponents.push(listingUUID);
				}

				assignContentDataObject[listingUUID] = data;
			});
		} else {
			// this.props.listingsGroups = listing_collection.*
			this.props.listingGroups.forEach((listingGroup) => {
				let data = {
					componentID: listingGroup.uuid,
					name: listingGroup.name.length ? listingGroup.name : listingGroup.uuid,
					checked: false,
					nonExclusive: listingGroup.nonExclusive,
				};

				if (isEdit && this.props.listingCollectionHasListingGroupRelation(listingGroup, this.props.listing)) {
					data.checked = true;
					if (!associatedComponents.includes(listingGroup.uuid)) {
						associatedComponents.push(listingGroup.uuid);
					}
				} else if (!unassociatedComponents.includes(listingGroup.uuid) && !associatedComponents.includes(listingGroup.uuid)) {
					unassociatedComponents.push(listingGroup.uuid);
				}

				assignContentDataObject[listingGroup.uuid] = data;
			});
		}

		if (this.state.createNewListingAfterThisOne) {
			assignContentData = placeholderContentData;
		} else {
			assignContentData = Object.values(assignContentDataObject);
		}

		// sort assignContentData
		assignContentData.sort((a, b) => {
			if (a.name < b.name) {
				return -1;
			}
			if (a.name > b.name) {
				return 1;
			}
			return 0;
		});

		const elms = [];
		const candidateElements = [];

		for (const [entityKey, info,] of Object.entries(activeListingConfig)) {
			if (entityKey.startsWith('listing_column.')) {
				// Guard the config lookup: a freshly-migrated config can be missing
				// from a stale listingConfigMap (DEV-12016) — an unguarded access
				// here took down the whole tree (white screen).
				const isAiGenerated = listingConfigMap[listingConfigUUID]?.[entityKey]?.ai_generation_config?.is_active;
				const triggerType = listingConfigMap[listingConfigUUID]?.[entityKey]?.ai_generation_config?.trigger_type;
				candidateElements.push({
					info,
					order: info.order,
					uuid: entityKey.split('.')[1],
					isAiGenerated,
					triggerType,
				});
			}
		}

		candidateElements.sort((a, b) => {
			if (a.order < b.order) {
				return -1;
			}
			if (a.order > b.order) {
				return 1;
			}
			return 0;
		});

		const requiredFields = [];

		// Columns only internal staff may see. The value is a predicate rather than a list of
		// role codes, so a custom role resolves correctly instead of matching nothing.
		const internalOnlyColumns = {
			Concatenator: hasGlobalScope,
			// add more columns and their visibility predicate as needed
		};

		candidateElements.forEach((candidate) => {
			let columnType = '';
			let columnDisplayName = '';
			Object.entries(candidate.info).forEach(([subKey, infoValue,]) => {
				if (subKey.startsWith('listing_column_type')) {
					columnType = infoValue.type;
					columnDisplayName = infoValue.display_name;
				}
			});

			if (internalOnlyColumns[columnDisplayName] && !internalOnlyColumns[columnDisplayName]()) {
				return;
			}

			let fieldType = '';

			let entity = {
				attr: {
					label: candidate.info.display_name,
				},
				name: `listing.${listing.uuid}.listing_column.${candidate.uuid}.${columnType}`,
				type: 'FIELD',
			};

			if (!isEdit && candidate.isAiGenerated) {
				entity = {
					...entity,
					attr: {
						...entity?.attr,
						htmlattr: {
							disabled: true,
						},
					},
				};
			} else if (isEdit &&
				candidate.isAiGenerated &&
				candidate.triggerType === 'onUpdate') {
				entity = {
					...entity,
					attr: {
						...entity?.attr,
						htmlattr: {
							disabled: true,
						},
					},
				};
			}

			if (listingConfigIsIntegration) {
				entity.isDisabled = true;
			}

			switch (columnType) {
				case 'string':
					// TODO: add columnDisplayName === 'PDF' in phase 2
					if (columnDisplayName === 'Image' || columnDisplayName === 'Logo' || columnDisplayName === 'Photo') {
						fieldType = 'custom.vulture.elements.uploader';
							entity.type = 'Image';
						entity.attr.uuid = `${listing.uuid}/${candidate.uuid}`;
					} else if (columnDisplayName === 'PDF') {
						fieldType = 'custom.vulture.elements.uploader';
							entity.type = 'PDF';
						entity.attr.uuid = `${listing.uuid}/${candidate.uuid}`;
					} else if (columnDisplayName === 'Video') {
						fieldType = 'custom.vulture.elements.uploader';
							entity.type = 'Video';
						entity.attr.uuid = `${listing.uuid}/${candidate.uuid}`;
					} else if (columnDisplayName === 'QR Code') {
						fieldType = 'custom.vulture.qrcode';
						entity.attr.parent_directory = 'ts-listings';
						entity.attr.uuid = `${listing.uuid}/${candidate.uuid}`;
					} else if (columnDisplayName === 'SMS Message') {
						fieldType = 'custom.vulture.elements.phone.number';
					} else if (columnDisplayName === 'Email Message' || columnDisplayName === 'Teams') {
						fieldType = 'custom.vulture.elements.email';
					} else if (columnDisplayName === 'Slack') {
						fieldType = 'custom.vulture.elements.slack';
					} else if (columnDisplayName === 'Array') {
						entity.options = [];

						const arrayOptions = listingFD[`account.${accountUUID}`].array_options;

						if (arrayOptions && JSON.parse(arrayOptions) && JSON.parse(arrayOptions)[candidate.info.display_name]) {
							const parsedOptions = JSON.parse(arrayOptions);

							if (parsedOptions[candidate.info.display_name] && parsedOptions[candidate.info.display_name].options && parsedOptions[candidate.info.display_name].type) {
								entity.options = parsedOptions[candidate.info.display_name].options;

								const entityNameWithoutType = entity.name.split('.').slice(0, 4).join('.');
								entity.name = `${entityNameWithoutType}.${parsedOptions[candidate.info.display_name].type}`;
							}
						}

						fieldType = 'custom.vulture.elements.drop.down';
					} else if (columnDisplayName === 'Building Picker') {
						entity.options = [
							{
								label: 'No Building',
								value: '',
							},
						];

						buildingsDataArray.forEach((building) => {
							entity.options.push({
								label: building.vals.displayName,
								value: building.m1Key.replace('building.', ''),
							});
						});

						fieldType = 'custom.vulture.elements.drop.down';
					} else if (columnDisplayName === 'Concatenator') {
						fieldType = 'custom.vulture.elements.text.small';

						entity = {
							...entity,
							attr: {
								...entity?.attr,
								htmlattr: {
									disabled: true,
								},
							},
						};
					} else if (columnDisplayName === 'Text Large') {
						fieldType = 'custom.vulture.elements.text.large';
					} else if (columnDisplayName === 'Location (apt/suite)') {
						elms.push(
							<LandmarkIDField
								key={`listing.${listing.uuid}.listing_column.${candidate.uuid}`}
								app={app}
								fd={listingFD}
								entityName={entity.name}
								label={candidate.info.display_name || columnDisplayName}
							/>);
						return;
					} else {
						fieldType = 'custom.vulture.elements.text.small';
					}
					break;
				case 'int':
					if (columnDisplayName === 'Directional Arrows') {
						fieldType = 'custom.vulture.elements.icon';
						entity.type = 'Directional Arrows';
					} else {
						fieldType = 'custom.vulture.elements.number';
					}
					break;
				case 'bool':
					fieldType = 'custom.vulture.elements.advanced.checkbox';
					break;
				case 'float':
					fieldType = 'custom.vulture.elements.float';
					break;
				case 'datetime':
					fieldType = 'custom.common.date';
					break;
				case 'time':
					fieldType = 'custom.common.date.time';
					break;
				case 'json':
					if (columnDisplayName === 'Primary Phone' || columnDisplayName === 'Phone') {
						fieldType = 'custom.vulture.elements.phone.number';
					}
					break;
				default:
					console.log(listing.uuid + ': Invalid type ' + columnType);
					break;
			}

			entity.fieldType = fieldType;

			requiredFields.push(entity);

			elms.push(
				<React.Fragment key={`listing.${listing.uuid}.listing_column.${candidate.uuid}`}>
					{app.renderEntity(entity, {
						fd: listingFD,
					})}
				</React.Fragment>
			);
		});

		this.setState({
			elms,
			assignContentData,
			associatedComponents,
			unassociatedComponents,
			requiredFields,
			originalAssociatedComponents: JSON.parse(JSON.stringify(associatedComponents)),
			createNewListingAfterThisOne: this.state.createNewListingAfterThisOne ? true : false,
		});
	}

	getSystem(m1, m1UUID) {
		const {
			listingFD,
		} = this.props;

		let parentM1Keys = [];
		for (let [m1Key, m1Data,] of Object.entries(listingFD)) {
			for (let m2Key of Object.keys(m1Data)) {
				if (m2Key === `${m1}.${m1UUID}`) {
					if (m1Key.startsWith('system.')) {
						return m1Key.split('.')[1];
					}

					parentM1Keys.push(m1Key);
				}
			}
		}

		for (let m1Key of parentM1Keys) {
			let systemUUID = this.getSystem(...m1Key.split('.'));
			if (typeof systemUUID === 'string') {
				return systemUUID;
			}
		}
	}

	getListingUUIDs(m1, m1UUID) {
		const {
			listingFD,
		} = this.props;

		let listingUUIDs = [];
		for (let [m2Key, m2Data,] of Object.entries(listingFD[`${m1}.${m1UUID}`])) {
			if (m2Key.startsWith('listing.')) {
				let m2UUID = m2Key.split('.')[1];
				listingUUIDs.push(m2UUID);

				for (let m3Key of Object.keys(m2Data)) {
					if (m3Key.startsWith('listing.')) {
						let childListingUUIDs = this.getListingUUIDs('listing', m2UUID);
						listingUUIDs = [...listingUUIDs, ...childListingUUIDs,];
					}
				}
			}
		}

		return listingUUIDs;
	}


	getTier1Parent(listingUUID) {
		const {
			listingFD,
		} = this.props;
		let uuidsToReturn = [];

		const parentEntityKeys = [];

		for (let [m1Key, m1Data,] of Object.entries(listingFD)) {
			for (let [m2Key, m2Data,] of Object.entries(m1Data)) {
				const entityKey = `${m1Key}.${m2Key}`;
				if (m2Key === `listing.${listingUUID}`) {
					parentEntityKeys.push(entityKey);
				}
			}
		}

		parentEntityKeys.forEach((entityKey) => {
			if (entityKey.startsWith('listing_collection')) {
				let m1ID = entityKey.split('.')[1];
				if (getFDValue(listingFD, `listing_collection.${m1ID}.listing.${listingUUID}`)) {
					uuidsToReturn.push(m1ID);
				}
			} else if (entityKey.startsWith('listing')) {
				let m1ID = entityKey.split('.')[1];
				uuidsToReturn = this.getTier1Parent(m1ID);
			}
		});

		return uuidsToReturn;
	}

	updateDays(day) {
		const {
			scheduling,
			schedulingMode,
		} = this.state;

		const isAdding = !(scheduling.days || []).includes(day);
		let days = [...(scheduling.days || [])];
		if (days.includes(day)) {
			days = days.filter((d) => d !== day);
		} else {
			days.push(day);
		}

		days = days.sort((a, b) => a - b);

		this.setState({
			scheduling: {
				...scheduling,
				days,
			},
		}, () => {
			if (isAdding && schedulingMode === 'specific-days-with-times' && this.timeRangesByDayRef.current) {
				this.timeRangesByDayRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			}
		});
	}

	getAssignContentData() {
		let elms = [];
		let groupName = null;
		let childClassName = style.groupName;

		Array.prototype.alphaSort = function (sortParameter) {
			function compare(aB, bB) {
				let a = aB[sortParameter];
				let b = bB[sortParameter];

				a = a.toLowerCase();
				b = b.toLowerCase();

				return a > b ? 1 : -1;
			}
			this.sort(compare);
		};

		//sort this before displaying the text
		this.state.assignContentData.alphaSort('name');
		this.state.assignContentData.map((potentialParentListings, i) => {
			if (!this.props.activeListingConfig.root) {
				let groupNameToDisplay = '';
				let groupNamesToAlphabetize = [];
				let tier1Parents = this.getTier1Parent(potentialParentListings.componentID);

				tier1Parents.forEach((parentUUID) => {
					this.props.listingGroups.forEach((group) => {
						if (group.uuid === parentUUID) {
							groupNameToDisplay += `${group.name}, `;
							groupNamesToAlphabetize.push(group.name);
						}
					});
				});
				groupNameToDisplay = groupNamesToAlphabetize.sort((a, b) => {
					let aA = a.toLowerCase();
					let bA = b.toLowerCase();
					if (bA > aA) {
						return -1;
					}
					if (aA < bA) {
						return 1;
					}
					return 0;
				}
				).join(', ');
				groupName = (
					<div className={style.contentGroupName} data-test-id="contentGroupName">{groupNameToDisplay}</div>
				);
				childClassName = `${style.groupName} ${style.childGroupName}`;
			}

			const ref = React.createRef();
			const handleClick = () => {
				return ref.current.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			};
			elms.push(
				<li data-selector="content-item"
					className={childClassName}
					key={`listings-li-${i}`}
					ref={ref}
					id={potentialParentListings.componentID}
					onClick={(e) => {
						let assignContentData = this.state.assignContentData;
						let checked = assignContentData[i].checked;
						assignContentData[i].checked = !checked;

						let selectedContentCount = this.state.selectedContentCount;

						// list of listing_collection UUIDs
						let associatedComponents = this.state.associatedComponents;

						let unassociatedComponents = this.state.unassociatedComponents;
						if (assignContentData[i].checked) {
							selectedContentCount++;
							associatedComponents.push(potentialParentListings.componentID);
							if (unassociatedComponents.includes(potentialParentListings.componentID)) {
								let indexToRemove = unassociatedComponents.indexOf(potentialParentListings.componentID);
								unassociatedComponents.splice(indexToRemove, 1);
							}
						}
						if (!assignContentData[i].checked) {
							selectedContentCount--;
							unassociatedComponents.push(potentialParentListings.componentID);
							if (associatedComponents.includes(potentialParentListings.componentID)) {
								let indexToRemove = associatedComponents.indexOf(potentialParentListings.componentID);
								associatedComponents.splice(indexToRemove, 1);
							}
						}

						this.setState({
							selectedContentCount,
							assignContentData,
							associatedComponents,
							unassociatedComponents,
						});
					}}
				>

					<div className={style.nameContainer}>
						<CustomCheckbox
							checked={potentialParentListings.checked}
						/>
						<div data-selector="content-name" className={`${style.contentBucket} ${style.contentBucketParent}`}>{potentialParentListings.name}</div>
					</div>
					{groupName}
				</li>
			);
		});

		return elms;
	}

	isDetailsEmpty() {
		const {
			listingFD,
		} = this.props;

		let numberOfFields = this.state.requiredFields.length;
		let numberOfEmptyFields = 0;

		this.state.requiredFields.forEach((field) => {
			if (!getFDValue(listingFD, field.name)) {
				numberOfEmptyFields++;
			}
		});

		if (numberOfEmptyFields === numberOfFields) {
			return true;
		}
		return false;
	}

	isFirstColumnEmpty() {
		const {
			listingFD,
			pendingChanges,
		} = this.props;

		const {
			requiredFields,
		} = this.state;

		let firstColumnValue = getFDValue(listingFD, requiredFields[0].name);
		firstColumnValue = firstColumnValue || pendingChanges[requiredFields[0].name];

		if (firstColumnValue === undefined || firstColumnValue === '') {
			return true;
		}

		return false;
	}

	checkURLFormat() {
		const {
			listingFD,
			pendingChanges,
		} = this.props;

		const pattern = /listing_column\.([a-zA-Z0-9-]+)\./;
		let urlColumnTypeUUID = '';

		// Gets UUID of listing_column_type with display_name 'URL'
		for (const [m1Key, m1Data,] of Object.entries(listingFD)) {
			if (m1Key.startsWith('listing_column_type')) {
				if (m1Data.display_name === 'URL') {
					urlColumnTypeUUID = m1Key.split('.')[1];

					break;
				}
			}
		}

		// For each pending change, if the column type is a URL, check if the value starts with https:// or is empty
		for (const [m1Key, m1Data,] of Object.entries(pendingChanges)) {
			const match = m1Key.match(pattern);

			if (match && match.length >= 2) {
				const urlColumnUUID = match[1];
				let columnTypeUUID = '';
				for (const [m2Key, m2Data,] of Object.entries(listingFD)) {
					if (m2Key.startsWith(`listing_column.${urlColumnUUID}`)) {
						for (const m3Key of Object.keys(m2Data)) {
							if (m3Key.startsWith('listing_column_type')) {
								columnTypeUUID = m3Key.split('.')[1];

								break;
							}
						}
					}
				}

				if (columnTypeUUID === urlColumnTypeUUID) {
					if (!m1Data.toLowerCase().startsWith('https://') && m1Data) {
						return false;
					}
				}
			}
		}

		return true;

	}

	activateRow(info, e) {
		const interactive = e.target.closest('button, input, label, a, select, textarea');

		if (interactive && e.currentTarget.contains(interactive)) {
			return;
		}

		this.setCheckbox(info);
	}

	setCheckbox(info) {
		const {
			app,
		} = this.props;

		let obj = {};

		let nonExclusiveSystemPresent = false;

		this.filteredListingGroups
			.forEach((listingGroup) => {
				if (nonExclusiveSystemPresent) {
					return;
				}

				for (const listingCollectionM1Key of info.listingCollectionM1Keys) {
					if (listingGroup.listingCollectionM1Keys.includes(listingCollectionM1Key)) {
						nonExclusiveSystemPresent = true;
						break;
					}
				}
			});

		const cb = () => {
			if (info.t1) {
				for (let listingCollectionM1Key of info.listingCollectionM1Keys) {
					obj[`${listingCollectionM1Key}.${info.listingM1Key}`] = !info.checked;
				}
			}

			if (!info.t1) {
				obj[`${info.parentM1Key}.${info.listingM1Key}`] = !info.checked;
			}

			info.checked = Object.values(obj)[0];

			app.setValue('listings', Object.keys(obj), Object.values(obj));
		};

		if (nonExclusiveSystemPresent) {
			this.setState({
				confirmModal: true,
				confirmModalContent: (
					<>
						<p>{`You are ${!info.checked ? 'assigning this listing to' : 'unassigning this listing from'} a content group that is shared by other system groups.
						Please be aware that this action will affect other systems.`}</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={(e) => {
								e.preventDefault();
								this.setState({
									confirmModal: false,
									confirmModalContent: '',
								}, () => {
									cb();
								});
							}}>Yes</button>
							<button className="button" onClick={(e) => {
								this.setState({
									confirmModal: false,
								});
							}}>No</button>
						</div>
					</>
				),
			});
		} else {
			return cb();
		}
	}

	setAllCheckboxes(isChecked) {
		const {
			listingParents,
			associatedComponents,
		} = this.state;

		const {
			app,
		} = this.props;

		let keys = [];
		let values = [];

		let nonExclusiveSystemPresent = false;

		this.filteredListingGroups
			.forEach((listingGroup) => {
				if (associatedComponents.includes(listingGroup.uuid)) {
					nonExclusiveSystemPresent = true;
				}
			}, []);

		const cb = () => {
			for (let info of listingParents) {
				let obj = {};
				if (info.t1) {
					for (let listingCollectionM1Key of info.listingCollectionM1Keys) {
						obj[`${listingCollectionM1Key}.${info.listingM1Key}`] = isChecked;
					}
				} else {
					obj[`${info.parentM1Key}.${info.listingM1Key}`] = isChecked;
				}
				info.checked = isChecked;
				keys.push(...Object.keys(obj));
				values.push(...Object.values(obj));
			}

			app.setValue('listings', keys, values);
		};

		if (nonExclusiveSystemPresent) {
			this.setState({
				confirmModal: true,
				confirmModalContent: (
					<>
						<p>{`You are ${isChecked ? 'assigning this listing to' : 'unassigning this listing from'} a content group that is shared by other system groups.
						Please be aware that this action will affect other systems.`}</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={(e) => {
								e.preventDefault();
								this.setState({
									confirmModal: false,
									confirmModalContent: '',
								}, () => {
									cb();
								});
							}}>Yes</button>
							<button className="button" onClick={(e) => {
								this.setState({
									confirmModal: false,
								});
							}}>No</button>
						</div>
					</>
				),
			});
		} else {
			return cb();
		}
	}

	render() {
		const {
			listingFD,
			listingConfigUUID,
			app,
			systemGroupUUID,
			listingConfigIsIntegration,
			fetchListingDataByListingConfig,
			removeListingsFromFD,
		} = this.props;

		let {
			scheduled,
			scheduling,
			schedulingMode,
			associatedComponents,
		} = this.state;

		// True for ANY role narrowed below account level, content managers included — the old
		// name isPropertyManager was already inaccurate about that.
		let isScopedBelowAccount = isNarrowScoped();

		// Check if use_all_listings is active (inherited from this config or any parent)
		const isUseAllListingsActive = !!listingFD[`listing_config.${listingConfigUUID}`]?.use_all_listings_inherited;

		let parentSelected = false;
		for (let parentObj of this.state.listingParents) {
			if (parentObj.checked) {
				parentSelected = true;
				break;
			}
		}

		const {
			listingParents,
		} = this.state;

		let checkContainer = <></>;
		if (!this.props.isEdit) {
			checkContainer =
				<div className={style.checkContainer}>
					<div className={style.checkbox} data-test-id="createAnotherCheckbox" onClick={(e) => {
						e.preventDefault();

						dlToggleCreateAnotherListing({
							option: !this.state.createNewListingAfterThisOne,
						});

						this.setState({
							createNewListingAfterThisOne: !this.state.createNewListingAfterThisOne,
						});
					}}>
						<CustomCheckbox
							checked={this.state.createNewListingAfterThisOne}
						/>
					</div>

					<p className={style.listingAdd}>Create another listing after this one</p>
				</div>;
		}

		let bottomButton =
			<button
				onClick={(e) => {
					if (this.props.app.hasPendingChanges()) {
						this.setState({
							confirmModal: true,
							confirmModalContent:
								<>
									<p>By cancelling you will lose your unsaved changes. Are you sure you want to continue?</p>
									<div className="confirm-modal-buttons">
										<button className="button-outlined" onClick={(e) => {
											this.setState({
												confirmModal: false,
											});
											app.closeModal();
										}}>Yes</button>
										<button className="button" onClick={(e) => {
											this.setState({
												confirmModal: false,
											});
										}}>No</button>
									</div>
								</>,
						});
					} else {
						app.closeModal();
					}
				}}
				className="button-outlined"
			>
				Cancel
			</button>;

		let saveButton = (
			<button
				onClick={(e) => {
					if (this.isFirstColumnEmpty()) {
						alert('You must enter data into the first column to continue saving.');
						return;
					}

					if (isScopedBelowAccount && !isUseAllListingsActive) {
						if (!parentSelected) {
							alert('You must assign content to at least one item from the list on the right.  If no items exist, the property you are filtering by does not have permission to add to these listings.');
							return;
						}
					}

					if (!this.checkURLFormat()) {
						alert('URLs must start with HTTPS://');
						return;
					}

					e.preventDefault();

					let listingParents = [];
					let saveMethod = 'saveDataAndCloseModal';
					if (this.state.createNewListingAfterThisOne) {
						listingParents = this.state.listingParents;
						saveMethod = 'saveData';
					}

					let nextUUID = uuidv4();

					let keys = [];
					let vals = [];
					for (let parentObj of listingParents) {
						if (parentObj.t1) {
							for (let listingCollectionM1Key of parentObj.listingCollectionM1Keys) {
								keys.push(`${listingCollectionM1Key}.listing.${nextUUID}`);
								vals.push(parentObj.checked);
							}
						}

						if (!parentObj.t1) {
							keys.push(`${parentObj.parentM1Key}.listing.${nextUUID}`);
							vals.push(parentObj.checked);
						}
					}

					app[saveMethod]('listings', async () => {
						console.log('SAVE');

						if (this.state.createNewListingAfterThisOne) {
							const firstChecked = this.state.listingParents.find((item) => item.checked);
							let scrollToTarget = null;
							if (firstChecked && firstChecked.parentM1Key && this.scrollToRef.current) {
								scrollToTarget = this.scrollToRef.current.querySelector(`[data-parent-m1-key="${firstChecked.parentM1Key}"]`);
							}

							if (scrollToTarget) {
								scrollToTarget.scrollIntoView({
									behavior: 'smooth',
								});
							}

							this.setState({
								listing: {
									uuid: nextUUID,
									parent_listings: [],
								},
							}, () => {
								app.setValue('', keys, vals, () => {
									this.makeMaps();
									this.makeFields();
								});
							});
						}
					});
				}}
				className={`button ${!app.hasPendingChanges() ? 'button-disabled no-click' : ''}`}
			>
				<CustomIcon icon='save' /> Save
			</button>
		);

		if (this.props.isEdit) {
			bottomButton =
				<button
					onClick={(e) => {
						const children = [];
						let nonExclusiveSystemPresent = false;

						this.filteredListingGroups
							.forEach((listingGroup) => {
								if (associatedComponents.includes(listingGroup.uuid)) {
									nonExclusiveSystemPresent = true;
								}
							}, []);


						for (let [m1Key, m1Data,] of Object.entries(listingFD)) {
							if (m1Key === `listing.${this.state.listing.uuid}`) {
								for (let [m2Key, m2Data,] of Object.entries(m1Data)) {
									const sections = m2Key.split('.').length;
									const entityKey = `${m1Key}.${m2Key}`;
									if (m2Key.startsWith('listing.') && sections === 2) {
										children.push(entityKey);
									}
								}
							}
						}

						let numberOfChildren = children.length;
						if (nonExclusiveSystemPresent) {
							this.setState({
								confirmModal: true,
								confirmModalContent: (
									<>
										<p>Cannot delete listings that are assigned to systems outside this group.</p>
										<div className="confirm-modal-buttons">
											<button className="button" onClick={(e) => {
												e.preventDefault();
												this.setState({
													confirmModal: false,
													confirmModalContent: '',
												});
											}}>Okay</button>
										</div>
									</>
								),
							});
						} else if (numberOfChildren > 0) {
							let message = `This listing currently has ${numberOfChildren} record(s) associated to it. Please delete the associated record(s) first.`;
							this.setState({
								confirmModal: true,
								confirmModalContent: (
									<>
										<p>{message}</p>
										<div className="confirm-modal-buttons">
											<button className="button" onClick={(e) => {
												e.preventDefault();
												this.setState({
													confirmModal: false,
													confirmModalContent: '',
												});
											}}>Okay</button>
										</div>
									</>
								),
							});
						} else {
							this.setState({
								confirmModal: true,
								confirmModalContent:
									<>
										<p>Are you sure you want to delete this listing?</p>
										<div className="confirm-modal-buttons">
											<button className="button-outlined" onClick={(e) => {
												if (systemGroupUUID) {
													const {
														listingParents,
													} = this.state;

													let keys = [];
													let values = [];

													for (let info of listingParents) {
														let obj = {};
														if (info.t1) {
															for (let listingCollectionM1Key of info.listingCollectionM1Keys) {
																obj[`${listingCollectionM1Key}.${info.listingM1Key}`] = false;
																obj[`${listingCollectionM1Key}.listing_config.${listingConfigUUID}`] = true;
															}
														} else {
															obj[`${info.parentM1Key}.${info.listingM1Key}`] = false;
														}
														info.checked = false;
														keys.push(...Object.keys(obj));
														values.push(...Object.values(obj));
													}

													app.setValue('listings', keys, values, () => {
														app.saveData('listings', () => {
															app.closeModal(() => {
																fetchListingDataByListingConfig(false);
															});
														});
													});
												} else {
													deleteListings([this.state.listing.uuid,])
														.then(() => {
															app.closeModal(() => {
																// Purge locally so the merge-based refetch can't resurrect a
																// truly-deleted row, then refetch so the UI self-corrects if
																// the server delete actually failed (deleteListings resolves
																// even on a failed request).
																if (removeListingsFromFD) {
																	removeListingsFromFD([this.state.listing.uuid,]);
																}
																fetchListingDataByListingConfig(false);
															});
														});
												}
											}}>Yes</button>
											<button className="button" onClick={(e) => {
												this.setState({
													confirmModal: false,
												});
											}}>No</button>
										</div>
									</>,
							});
						}
					}}
					className="button-outlined"
				>
					<CustomIcon
						icon='trash'
					/> Delete
				</button>;
		}

		let confirmModal = '';
		if (this.state.confirmModal === true) {
			confirmModal =
				<div className="confirm-modal-container">
					<div className="confirm-modal-content">
						{this.state.confirmModalContent}
					</div>
				</div>;
		}

		let schedulingElm = '';

		if (this.props.listingConfigHasScheduling) {
			schedulingElm = <>
				<div className={style.sectionLabel} ref={this.schedulingSectionRef}>Scheduling</div>
				<div className={style.scheduling}>
					<div className={style.contentToggle}>
						<input
							className='on-off on-off-label'
							data-test-id='listingUseSchedulingToggle'
							type='checkbox'
							checked={scheduled}
							onChange={(e) => {
								const newScheduled = !scheduled;
								this.setState({
									scheduled: newScheduled,
								}, () => {
									if (newScheduled && this.schedulingSectionRef.current) {
										this.schedulingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
									}
								});
							}}
						/> Use Scheduling
					</div>

					{scheduled && (
						<>
							<div className={`text-small ${style.description}`}>
								<div className={`label ${style.label}`}>
									<label>Active Dates</label>
								</div>
								<div className={`${style.datePicker} ${style.show}`}>
									<div className={style.startDate}>
										<DatePicker
											showIcon
											selected={formateDateForDatePicker(scheduling.start_date)}
											placeholderText="Active Now"
											minDate={new Date()}
											maxDate={scheduling.end_date ? Date.parse(scheduling.end_date) : null}
											showTimeSelect
											timeFormat="h:mm aa"
											timeIntervals={15}
											dateFormat="MM/dd/yyyy h:mm aa"
											onChange={(e) => {
												let formattedDate = formatDateForJSON(e);
												this.setState({
													scheduling: {
														...scheduling,
														start_date: formattedDate,
													},
												});
											}}
										/>
									</div>
									<div className={style.endDate}>
										<DatePicker
											showIcon
											selected={formateDateForDatePicker(scheduling.end_date)}
											placeholderText="No Expiration"
											minDate={scheduling.start_date ? Date.parse(scheduling.start_date) : new Date()}
											minTime={
												scheduling.start_date && scheduling.end_date &&
													new Date(scheduling.start_date).toDateString() === new Date(scheduling.end_date).toDateString() ?
													new Date(scheduling.start_date) :
													new Date().setHours(0, 0, 0, 0)
											}
											maxTime={new Date().setHours(23, 59, 59, 999)}
											showTimeSelect
											timeFormat="h:mm aa"
											timeIntervals={15}
											dateFormat="MM/dd/yyyy h:mm aa"
											onChange={(e) => {
												let newEndDate;
												if (scheduling.start_date && e <= new Date(scheduling.start_date)) {
													newEndDate = formatDateForJSON(new Date(new Date(scheduling.start_date).getTime() + 15 * 60000));
												} else {
													newEndDate = formatDateForJSON(e);
												}
												this.setState({
													scheduling: {
														...scheduling,
														end_date: newEndDate,
													},
												});
											}}
										/>
									</div>
									<button className={style.clearButton}
										onClick={() => {
											this.setState({
												scheduling: {
													...scheduling,
													start_date: null,
													end_date: null,
												},
											});
										}}
									>Clear</button>
								</div>
								<SchedulingHelperText />
							</div>

							<div className={`text-small ${style.description}`}>
								<div className={`label ${style.label}`}>
									<label>Display Schedule</label>
								</div>
								<div className={style.schedulingDropdownContainer} ref={this.schedulingModeDropdownRef}>
									<div
										className={style.schedulingDropdownHeader}
										onClick={this.toggleSchedulingModeDropdown}
									>
										<span className={style.schedulingDropdownHeaderText}>
											{getSchedulingModeDisplayText(schedulingMode)}
										</span>
										<CustomIcon
											icon={this.state.schedulingModeDropdownOpen ? 'caret-up' : 'caret-down'}
											className={style.schedulingDropdownCaret}
										/>
									</div>

									{this.state.schedulingModeDropdownOpen && (
										<div className={style.schedulingDropdownList}>
											<div
												className={style.schedulingDropdownItem}
												onClick={() => {
													// Recurrence data is left untouched here so switching back
													// restores it; save-time cleaning handles the '' mode.
													this.setState({
														schedulingMode:             '',
														schedulingModeDropdownOpen: false,
													});
												}}
											>
												<span
													className={style.schedulingDropdownItemLabel}
													style={getSchedulingPlaceholderStyle(schedulingMode)}
												>
													All Days Currently Active
												</span>
											</div>
											<div
												className={style.schedulingDropdownItem}
												onClick={() => {
													this.setState({
														schedulingMode: 'all-day-specific-days',
														schedulingModeDropdownOpen: false,
													});
												}}
											>
												<span className={style.schedulingDropdownItemLabel}>
													Show all day on specific days
												</span>
											</div>
											<div
												className={style.schedulingDropdownItem}
												onClick={() => {
													this.setState({
														schedulingMode: 'daily-time-range',
														schedulingModeDropdownOpen: false,
													});
												}}
											>
												<span className={style.schedulingDropdownItemLabel}>
													Show every day within specific time range
												</span>
											</div>
											<div
												className={style.schedulingDropdownItem}
												onClick={() => {
													this.setState({
														schedulingMode: 'specific-days-with-times',
														schedulingModeDropdownOpen: false,
													});
												}}
											>
												<span className={style.schedulingDropdownItemLabel}>
													Show on specific days within specific time ranges per day
												</span>
											</div>
										</div>
									)}
								</div>
							</div>

							{schedulingMode === 'all-day-specific-days' && (
								<div className={`text-small ${style.description}`}>
									<div className={`label ${style.label}`}>
										<label>Recurrence: Days of Week</label>
									</div>
									<WeekdaySelector
										selectedDays={scheduling.days}
										onDayToggle={(day) => {
											return this.updateDays(day);
										}}
										idSuffix="-listing"
									/>
								</div>
							)}

							{schedulingMode === 'daily-time-range' && (
								<div className={`text-small ${style.description}`}>
									<div className={`label ${style.label}`}>
										<label>Recurrence: Time Range</label>
									</div>
									<div className={`${style.timePicker} ${style.show}`}>
										<div className={style.startDate}>
											<DatePicker
												selected={formatTimeForPicker(scheduling.recurring_start_time)}
												placeholderText="12:00 AM"
												{...timePickerProps}
												onChange={(e) => {
													this.setState({
														scheduling: {
															...scheduling,
															recurring_start_time: handleStartTimeChange(e, scheduling.recurring_end_time),
														},
													});
												}}
											/>
										</div>
										<div className={style.endDate}>
											<DatePicker
												selected={formatTimeForPicker(scheduling.recurring_end_time)}
												placeholderText="11:59 PM"
												{...timePickerProps}
												onChange={(e) => {
													this.setState({
														scheduling: {
															...scheduling,
															recurring_end_time: handleEndTimeChange(e, scheduling.recurring_start_time),
														},
													});
												}}
											/>
										</div>
										<button className={style.clearButton}
											onClick={() => {
												this.setState({
													scheduling: {
														...scheduling,
														recurring_start_time: null,
														recurring_end_time: null,
													},
												});
											}}
										>Clear</button>
									</div>
								</div>
							)}

							{schedulingMode === 'specific-days-with-times' && (
								<>
									<div className={`text-small ${style.description}`}>
										<div className={`label ${style.label}`}>
											<label>Recurrence: Days of Week</label>
										</div>
										<WeekdaySelector
											selectedDays={scheduling.days}
											onDayToggle={(day) => {
												return this.updateDays(day);
											}}
											idSuffix="-listing-times"
										/>
									</div>

									{(scheduling.days || []).length > 0 && (
										<div className={`text-small ${style.description}`} ref={this.timeRangesByDayRef}>
											<div className={`label ${style.label}`}>
												<label>Time Ranges by Day</label>
											</div>
											{(scheduling.days || []).sort((a, b) => {
												return a - b;
											}).map((dayIndex) => {
												const dayName = dayMap[dayIndex];
												const dayScheduling = scheduling.recurring_times_by_day?.[dayIndex] || {
													use_recurring_times: true,
													start_time: null,
													end_time: null,
												};

												return (
													<div key={dayIndex} className={`${style.timePicker} ${style.show}`}>
														<span style={{
															minWidth: '45px',
															fontWeight: '600',
														}}>{dayName}:</span>
														<div className={style.startDate}>
															<DatePicker
																selected={formatTimeForPicker(dayScheduling.start_time)}
																placeholderText="12:00 AM"
																{...timePickerProps}
																onChange={(e) => {
																	const newScheduling = {
																		...scheduling,
																	};
																	if (!newScheduling.recurring_times_by_day) newScheduling.recurring_times_by_day = {};
																	if (!newScheduling.recurring_times_by_day[dayIndex]) newScheduling.recurring_times_by_day[dayIndex] = {
																		use_recurring_times: true,
																	};
																	const dayEnd = newScheduling.recurring_times_by_day[dayIndex]?.end_time;
																	newScheduling.recurring_times_by_day[dayIndex].start_time = handleStartTimeChange(e, dayEnd);
																	this.setState({
																		scheduling: newScheduling,
																	});
																}}
															/>
														</div>
														<div className={style.endDate}>
															<DatePicker
																selected={formatTimeForPicker(dayScheduling.end_time)}
																placeholderText="11:59 PM"
																{...timePickerProps}
																onChange={(e) => {
																	const newScheduling = {
																		...scheduling,
																	};
																	if (!newScheduling.recurring_times_by_day) newScheduling.recurring_times_by_day = {};
																	if (!newScheduling.recurring_times_by_day[dayIndex]) newScheduling.recurring_times_by_day[dayIndex] = {
																		use_recurring_times: true,
																	};
																	const dayStart = newScheduling.recurring_times_by_day[dayIndex]?.start_time;
																	newScheduling.recurring_times_by_day[dayIndex].end_time = handleEndTimeChange(e, dayStart);
																	this.setState({
																		scheduling: newScheduling,
																	});
																}}
															/>
														</div>
														<button className={style.clearButton}
															onClick={() => {
																const newScheduling = {
																	...scheduling,
																};
																if (!newScheduling.recurring_times_by_day) newScheduling.recurring_times_by_day = {};
																if (!newScheduling.recurring_times_by_day[dayIndex]) newScheduling.recurring_times_by_day[dayIndex] = {
																	use_recurring_times: true,
																	start_time: null,
																	end_time: null,
																};
																newScheduling.recurring_times_by_day[dayIndex].start_time = null;
																newScheduling.recurring_times_by_day[dayIndex].end_time = null;
																this.setState({
																	scheduling: newScheduling,
																});
															}}
														>Clear</button>
													</div>
												);
											})}
										</div>
									)}
								</>
							)}
						</>
					)}
				</div>
			</>;
		}

		let column2Label = 'Assign Content';

		return (
			<>
				{confirmModal}
				<div className={style.listingsModal}>
					<div className={style.listing}>{this.state.name}</div>
					<div className={style.listing}>{this.state.suite}</div>
					<div className={`${style.listingsModalContainer} ${listingConfigIsIntegration && style.integrationModalContainer}`}>
						<div className={style.column1} style={isUseAllListingsActive ? { width: '100%' } : {}}>
							<div className={style.sectionLabel}>Details</div>
							{this.state.elms}
							{isUseAllListingsActive && schedulingElm}
						</div>
						{!isUseAllListingsActive && <div className={style.column2}>
							<div className={style.sectionLabel}>{column2Label}</div>
							{(() => {
								const {
									listingParents: lp,
									assignSearch,
								} = this.state;
								const assignTypeLabel = this.props.activeListingConfig?.root ? 'systems' : (this.props.parentsPrimaryColumnData?.header ? this.props.parentsPrimaryColumnData.header.toLowerCase() : 'parent listing(s)');

								const totalCount = lp.length;
								const selectedCount = lp.filter((info) => info.checked).length;
								const isAllAssigned = totalCount > 0 && lp.every((info) => info.checked);
								const isSomeAssigned = lp.some((info) => info.checked);

								const q = assignSearch.trim().toLowerCase();

								const hasParentGroups = !this.props.activeListingConfig.root;
								const isSearching = q.length > 0;

								const propertyGroups = [];
								const propertyGroupItems = {};

								if (!hasParentGroups && this.props.listingFD && this.props.listingGroups) {
									const fd = this.props.listingFD;
									Object.entries(fd).forEach(([key, val]) => {
										if (!key.startsWith('system_group.')) return;
										const sgUUID = key.split('.')[1];
										const sgName = (val && val.display_name) ? val.display_name : sgUUID;
										const systemIDsInProperty = new Set(
											Object.keys(val).filter((k) => k.startsWith('system.')).map((k) => k.split('.')[1])
										);
										const groupsInProperty = this.props.listingGroups.filter((g) =>
											g.systemM1Keys?.some((k) => systemIDsInProperty.has(k.split('.')[1]))
										);
										const groupUUIDSet = new Set(groupsInProperty.map((g) => g.uuid));
										const matchingParents = lp.filter((info) => groupUUIDSet.has(info.uuid));
										if (matchingParents.length > 0) {
											propertyGroups.push({ sgUUID, sgName });
											propertyGroupItems[sgUUID] = matchingParents;
										}
									});
									propertyGroups.sort((a, b) => a.sgName.localeCompare(b.sgName));
								}

								let filteredGrouped = null;
								if (q) {
									const groups = [];
									const coveredKeys = new Set();
									propertyGroups.forEach((pg) => {
										const pgItems = propertyGroupItems[pg.sgUUID] || [];
										const pgNameMatches = pg.sgName.toLowerCase().includes(q);
										const matchingItems = pgNameMatches
											? pgItems
											: pgItems.filter((info) => (info.label || '').toLowerCase().includes(q));
										if (matchingItems.length > 0) {
											groups.push({ pg, items: matchingItems });
											matchingItems.forEach((info) => coveredKeys.add(info.uuid || info.entityName || info.label));
										}
									});
									const ungrouped = lp.filter((info) => {
										const key = info.uuid || info.entityName || info.label;
										if (coveredKeys.has(key)) return false;
										return (info.label || '').toLowerCase().includes(q);
									});
									filteredGrouped = { groups, ungrouped };
								}

								const setPropertyCheckboxes = (pgItems, isChecked) => {
									const { app } = this.props;
									let keys = [];
									let values = [];

									let nonExclusiveSystemPresent = pgItems.some((info) =>
										this.filteredListingGroups.some((listingGroup) =>
											(info.listingCollectionM1Keys || []).some((k) =>
												listingGroup.listingCollectionM1Keys.includes(k)
											)
										)
									);

									const cb = () => {
										for (let info of pgItems) {
											let obj = {};
											if (info.t1) {
												for (let listingCollectionM1Key of info.listingCollectionM1Keys) {
													obj[`${listingCollectionM1Key}.${info.listingM1Key}`] = isChecked;
												}
											} else {
												obj[`${info.parentM1Key}.${info.listingM1Key}`] = isChecked;
											}
											info.checked = isChecked;
											keys.push(...Object.keys(obj));
											values.push(...Object.values(obj));
										}
										app.setValue('listings', keys, values);
									};

									if (nonExclusiveSystemPresent) {
										this.setState({
											confirmModal: true,
											confirmModalContent: (
												<>
													<p>{`You are ${isChecked ? 'assigning this listing to' : 'unassigning this listing from'} a content group that is shared by other system groups.
												Please be aware that this action will affect other systems.`}</p>
													<div className="confirm-modal-buttons">
														<button className="button" onClick={(e) => {
															e.preventDefault();
															this.setState({
																confirmModal: false,
																confirmModalContent: '',
															}, () => {
																cb();
															});
														}}>Yes</button>
														<button className="button" onClick={(e) => {
															this.setState({ confirmModal: false });
														}}>No</button>
													</div>
												</>
											),
										});
									} else {
										cb();
									}
								};

								return (
									<div className={style.assignInlinePanel}>
										<div className={style.assignInlineSearch}>
											<div className={style.assignInlineSearchWrapper}>
												<span className={style.assignInlineSearchIcon}><CustomIcon icon="search" /></span>
												<input
													type="text"
													value={assignSearch}
													onChange={(e) => this.setState({ assignSearch: e.target.value, })}
													placeholder={`Search ${totalCount} ${assignTypeLabel}...`}
												/>
												{selectedCount > 0 && (
													<span className={style.assignInlineSearchCount}>{selectedCount} selected</span>
												)}
											</div>
										</div>
										<div className={style.assignInlineActions}>
											<label className={style.assignInlineAssignAllLabel}>
												<input
													type="checkbox"
													checked={isAllAssigned}
													ref={(node) => { if (node) node.indeterminate = isSomeAssigned && !isAllAssigned; }}
													onChange={(e) => { this.setAllCheckboxes(e.target.checked); }}
													onClick={(e) => e.stopPropagation()}
												/>
												<span>Assign to all {totalCount} {assignTypeLabel}</span>
											</label>
											<button type="button" className="button" onClick={() => this.setAllCheckboxes(false)}>Clear All</button>
										</div>
										{!isSearching && propertyGroups.length > 0 && (
											<div className={style.assignInlineQuickJump}>
												<span className={style.assignInlineQuickJumpLabel}>Quick:</span>
												{propertyGroups.map((pg) => (
													<button
														type="button"
														key={pg.sgUUID}
														className={style.assignInlineQuickJumpItem}
														onClick={() => {
															const listEl = this.scrollToRef.current;
															const el = this.assignInlinePropertyRefs?.[pg.sgUUID];
															if (listEl && el) {
																listEl.scrollTo({ top: el.getBoundingClientRect().top - listEl.getBoundingClientRect().top + listEl.scrollTop, behavior: 'smooth' });
															}
														}}
													>
														{pg.sgName}
													</button>
												))}
											</div>
										)}
										<div className={style.assignInlineList} ref={this.scrollToRef}>
											{isSearching ? (
												<>
													{filteredGrouped && filteredGrouped.groups.map(({ pg, items }) => {
														const checkedCount = items.filter((info) => info.checked).length;
														const isPropertyAllChecked = items.length > 0 && items.every((info) => info.checked);
														const isPropertySomeChecked = items.some((info) => info.checked);
														return (
															<div key={`search-pg-${pg.sgUUID}`}>
																<div className={style.assignInlinePropertyHeader}>
																	<input
																		type="checkbox"
																		checked={isPropertyAllChecked}
																		ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
																		onChange={(e) => { setPropertyCheckboxes(items, e.target.checked); }}
																		onClick={(e) => e.stopPropagation()}
																	/>
																	<span className={style.assignInlinePropertyName}>{pg.sgName}</span>
																	<span className={style.assignInlinePropertyCount}>{checkedCount}/{items.length}</span>
																	<button
																		type="button"
																		className={`${style.assignInlineToggleLink} ${style.assignInlinePropertyToggle}`}
																		style={{ background: 'none', border: 'none', padding: 0 }}
																		onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPropertyCheckboxes(items, !isPropertyAllChecked); }}
																	>
																		{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
																	</button>
																</div>
																{items.map((info, i) => (
																	<label
																		key={`search-pg-${pg.sgUUID}-${info.entityName || i}`}
																		data-parent-m1-key={info.parentM1Key}
																		className={`${style.assignInlineItem} ${style.assignInlineItemIndented}`}
																	>
																		<input
																			type="checkbox"
																			checked={info.checked}
																			onChange={() => this.setCheckbox(info)}
																		/>
																		<span>{info.label}</span>
																	</label>
																))}
															</div>
														);
													})}
													{filteredGrouped && filteredGrouped.ungrouped.map((info, i) => {
														const rowId = `assign-ungrouped-${info.entityName || i}`;

														return (
															<div
																key={`search-ungrouped-${info.entityName || i}`}
																data-parent-m1-key={info.parentM1Key}
																className={hasParentGroups
																	? `${style.assignInlineItem} ${style.assignInlineItemIndented}`
																	: style.assignInlineItem
																}
																onClick={(e) => this.activateRow(info, e)}
															>
																<input
																	id={rowId}
																	type="checkbox"
																	checked={info.checked}
																	onChange={() => this.setCheckbox(info)}
																/>
																<div className={style.assignInlineItemText}>
																	<label htmlFor={rowId}><span>{info.label}</span></label>
																	{hasParentGroups && (
																		<ParentLabels labels={info.parentLabelsList} controlId={rowId} />
																	)}
																</div>
															</div>
														);
													})}
													{(!filteredGrouped || (filteredGrouped.groups.length === 0 && filteredGrouped.ungrouped.length === 0)) && <div className={style.assignInlineEmpty}>No {assignTypeLabel} found.</div>}
												</>
											) : propertyGroups.length > 0 ? propertyGroups.map((pg) => {
												const pgItems = propertyGroupItems[pg.sgUUID] || [];
												if (pgItems.length === 0) return null;
												const checkedCount = pgItems.filter((info) => info.checked).length;
												const isPropertyAllChecked = pgItems.length > 0 && pgItems.every((info) => info.checked);
												const isPropertySomeChecked = pgItems.some((info) => info.checked);
												return (
													<div key={pg.sgUUID}>
														<span ref={(el) => { this.assignInlinePropertyRefs[pg.sgUUID] = el; }} style={{ display: 'block', height: 0, }} />
														<div className={style.assignInlinePropertyHeader}>
															<input
																type="checkbox"
																checked={isPropertyAllChecked}
																ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
																onChange={(e) => { setPropertyCheckboxes(pgItems, e.target.checked); }}
																onClick={(e) => e.stopPropagation()}
															/>
															<span className={style.assignInlinePropertyName}>{pg.sgName}</span>
															<span className={style.assignInlinePropertyCount}>{checkedCount}/{pgItems.length}</span>
															<button
																type="button"
																className={`${style.assignInlineToggleLink} ${style.assignInlinePropertyToggle}`}
																style={{ background: 'none', border: 'none', padding: 0 }}
																onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPropertyCheckboxes(pgItems, !isPropertyAllChecked); }}
															>
																{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
															</button>
														</div>
														{pgItems.map((info, i) => (
															<label
																key={`pg-${pg.sgUUID}-${info.entityName || i}`}
																data-parent-m1-key={info.parentM1Key}
																className={`${style.assignInlineItem} ${style.assignInlineItemIndented}`}
															>
																<input
																	type="checkbox"
																	checked={info.checked}
																	onChange={() => this.setCheckbox(info)}
																/>
																<span>{info.label}</span>
															</label>
														))}
													</div>
												);
											}) : lp.map((info, i) => {
												const rowId = `assign-flat-${info.entityName || i}`;

												return (
													<div
														key={`inline-listing-flat-${info.entityName || i}`}
														data-parent-m1-key={info.parentM1Key}
														className={hasParentGroups
															? `${style.assignInlineItem} ${style.assignInlineItemIndented}`
															: style.assignInlineItem
														}
														onClick={(e) => this.activateRow(info, e)}
													>
														<input
															id={rowId}
															type="checkbox"
															checked={info.checked}
															onChange={() => this.setCheckbox(info)}
														/>
														<div className={style.assignInlineItemText}>
															<label htmlFor={rowId}><span>{info.label}</span></label>
															{hasParentGroups && (
																<ParentLabels labels={info.parentLabelsList} controlId={rowId} />
															)}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								);
							})()}
							{schedulingElm}
						</div>}
					</div>

					<div className={style.bottom}>
						{checkContainer}
						{bottomButton}
						{saveButton}
					</div>
				</div>

			</>
		);
	}
}

export default ListingsModal;
